/**
 * SignaturesHub — paid-plan-gated digital signatures.
 *
 * Thin page-level orchestrator. The actual UI is composed from four
 * extracted components in `/components/signatures/`:
 *   • RequestsTab — list of pending + signed signature requests
 *   • TemplatesTab — saved PDF templates with field layouts
 *   • NewRequestWizard — upload PDF, drag-place fields, send
 *   • UpsellCard — shown to free-plan workspaces
 *
 * This file owns:
 *   • Plan-status fetch (gates the entire page)
 *   • Cross-tab data fetch (requests / templates / folders / contacts)
 *   • Deep-link consumption (`?tab=new&contact_id=…&folder_id=…`)
 *   • Tab state + refresh-after-success orchestration
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileSignature } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
// Extracted presentational components — see /components/signatures/
import { UpsellCard } from '../components/signatures/UpsellCard';
import { TemplatesTab } from '../components/signatures/TemplatesTab';
import { RequestsTab } from '../components/signatures/RequestsTab';
import { NewRequestWizard } from '../components/signatures/NewRequestWizard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignaturesHub() {
  const { currentWorkspace } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const [planStatus, setPlanStatus] = useState({ esign_enabled: null });
  const [requests, setRequests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  // If URL says ?tab=new (e.g. deep-link from a contact's Contracts folder),
  // jump straight to the wizard instead of landing on Requests.
  const [tab, setTab] = useState(searchParams.get('tab') === 'new' ? 'new' : 'requests');

  const initialContactId = searchParams.get('contact_id') || '';
  const initialFolderId = searchParams.get('folder_id') || '';

  // After consuming the deep-link params, scrub them from the URL so a
  // subsequent refresh doesn't keep re-firing the pre-fill (and so the
  // address bar stays clean).
  useEffect(() => {
    if (searchParams.get('tab') || searchParams.get('contact_id') || searchParams.get('folder_id')) {
      // Clear after a tick so the wizard's effect can read them first.
      const t = setTimeout(() => {
        const next = new URLSearchParams();
        setSearchParams(next, { replace: true });
      }, 100);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    if (!currentWorkspace?.workspace_id) return;
    setLoading(true);
    try {
      // Plan status drives the entire page; the rest is best-effort.
      const planRes = await apiFetch(`${API_URL}/api/esign/plan-status`);
      const plan = planRes.ok ? await planRes.json() : { esign_enabled: false };
      setPlanStatus(plan);
      if (!plan.esign_enabled) return;
      const [reqRes, tplRes, foldRes, contactsRes] = await Promise.all([
        apiFetch(`${API_URL}/api/esign/requests?workspace_id=${currentWorkspace.workspace_id}`),
        apiFetch(`${API_URL}/api/esign/templates?workspace_id=${currentWorkspace.workspace_id}`),
        apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/folders`),
        apiFetch(`${API_URL}/api/contacts?workspace_id=${currentWorkspace.workspace_id}`),
      ]);
      if (reqRes.ok) setRequests(await reqRes.json());
      if (tplRes.ok) setTemplates(await tplRes.json());
      if (foldRes.ok) {
        const f = await foldRes.json();
        setFolders(Array.isArray(f) ? f : (f.folders || []));
      }
      if (contactsRes.ok) {
        const c = await contactsRes.json();
        setContacts(Array.isArray(c) ? c : (c.contacts || c.items || []));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentWorkspace?.workspace_id]);

  if (planStatus.esign_enabled === false) return <UpsellCard />;

  return (
    <div className="max-w-5xl mx-auto p-4 lg:p-6 space-y-4" data-testid="signatures-hub">
      <div className="flex items-center gap-2">
        <FileSignature className="w-6 h-6 text-emerald-600" />
        <div>
          <h1 className="text-xl font-semibold">Signatures</h1>
          <p className="text-xs text-muted-foreground">Send PDFs for legally-binding electronic signature</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="new" data-testid="tab-new">+ New Request</TabsTrigger>
        </TabsList>
        <TabsContent value="requests" className="mt-4">
          <RequestsTab requests={requests} loading={loading} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="templates" className="mt-4">
          <TemplatesTab templates={templates} loading={loading} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="new" className="mt-4">
          <NewRequestWizard
            folders={folders}
            templates={templates}
            contacts={contacts}
            onSent={() => { refresh(); setTab('requests'); }}
            initialContactId={initialContactId}
            initialFolderId={initialFolderId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
